import { useState, useEffect, ReactElement } from 'react'
import { CalendarMonthRounded } from '@mui/icons-material'
import {
  Stack,
  Typography,
  FormControl,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Select,
  SelectChangeEvent,
  ListSubheader,
  MenuItem,
  Grid,
  Button,
  Box,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { groupBy, range } from 'lodash'
import { formatDate } from '../../shared/utils'
import { useAppSelector } from '../../store/hooks'
import { diversityDimensionMap, fsColors, moduleConfigMap } from '../../shared/constants'
import { reportAPI } from '../../shared/api/ReportAPI'
import FsDemoAccountModal from '../common/molecules/DemoAccountModal'
import { getIcon } from '../common/Icons'

const FsGeneralMenu = ({
  onClose,
  setGenerationStatus,
}: {
  onClose: () => void
  setGenerationStatus: (status: string) => void
}) => {
  const [lastRunTime, setLastRunTime] = useState<Date | null>(null)
  const [endValue, setEndValue] = useState<Date | null>(null)
  const [selectedDiversityDimensions, setSelectedDiversityDimensions] = useState<string[]>([])
  const [selectedModule, setSelectedModule] = useState<string | null>(null)
  const [localeValue, setLocaleValue] = useState<'de' | 'en'>('de')
  const { t } = useTranslation()
  const { runs, modules, currentRun } = useAppSelector((state) => state.currentAnalysis) // modules active in the current clone
  const { currentLanguage, company, account, currentTheme } = useAppSelector(
    (state) => state.currentUser,
  )
  const [openDemoModal, setOpenDemoModal] = useState<boolean>(false)

  const handleCheckboxChange = (event: { target: { checked: any; value: any } }) => {
    const { checked, value } = event.target
    if (checked) {
      setSelectedDiversityDimensions([...selectedDiversityDimensions, value])
    } else {
      setSelectedDiversityDimensions(selectedDiversityDimensions.filter((sdd) => sdd !== value))
    }
  }

  const handleRadioButtonChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedModule(event.target.value)
  }

  useEffect(() => {
    if (runs) {
      const lastRun = runs[runs.length - 1]
      setEndValue(new Date(lastRun.runAt))

      setLastRunTime(lastRun.runAt)
    }
  }, [runs])

  const closeDrawer = () => {
    setEndValue(lastRunTime)
    setLocaleValue('de')
    onClose()
  }

  const filteredRuns = runs ? runs?.filter((r) => r !== null || r !== undefined) : []

  const groupedDates = groupBy(filteredRuns.reverse(), (run) =>
    formatDate(run.runAt, currentLanguage, {
      year: 'numeric',
    }),
  )

  const latestRun = runs ? runs[runs.length - 1] : null

  const groupedDateComponents: ReactElement[] = []
  Object.keys(groupedDates)
    .sort((a, b) => parseInt(b, 10) - parseInt(a, 10))
    .forEach((year, yindex) => {
      groupedDateComponents.push(<ListSubheader key={year}>{year}</ListSubheader>)
      groupedDates[year].forEach((run, index) => {
        groupedDateComponents.push(
          <MenuItem key={`${year}_${index}`} value={`${run.id}`}>{`${formatDate(
            run.runAt,
            currentLanguage,
          )}${index === 0 && yindex === 0 ? ` (${t('latest.date')})` : ''}`}</MenuItem>,
        )
      })
    })
  const isDemoAccount = process.env.REACT_APP_SLUG === 'demo' && !account?.isSuperuser // getIsDemoAccount()

  const generateReport = async () => {
    if (isDemoAccount) {
      setOpenDemoModal(true)
      return
    }

    if (endValue && currentRun && selectedModule && selectedDiversityDimensions.length > 0) {
      setGenerationStatus('loading')
      try {
        await reportAPI.generateReport(
          selectedModule,
          'general',
          {
            diversityDimensions: selectedDiversityDimensions,
            shortName: company,
            name: company,
            primary: fsColors.fsDarkBlue.colors[0],
            secondary: fsColors.fsDarkBlue.colors[1],
            tertiary: fsColors.fsDarkBlue.colors[2],
            contrast: fsColors.fsOrange.colors[0],
          },
          endValue ?? currentRun.runAt,
          '1',
          localeValue,
        )
        setGenerationStatus('success')
      } catch (error: any) {
        setGenerationStatus('failed')
      }
    }
  }

  return (
    <Stack>
      <FsDemoAccountModal isOpen={openDemoModal} setIsOpen={setOpenDemoModal} />
      <Typography variant="h3" className="mt-3 mb-2">
        {t('module')}
      </Typography>

      {modules ? (
        <>
          <RadioGroup>
            <Grid container spacing={2} my={1}>
              {range(0, modules.length).map((i) => (
                <Grid size={{ xs: 6 }} key={i}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Radio
                          value={modules[i].name}
                          onChange={handleRadioButtonChange}
                          checked={selectedModule === `${modules[i].name}`}
                        />
                      }
                      label={
                        <Box mt={1}>
                          {getIcon(modules[i].name, {
                            color: moduleConfigMap.get(modules[i].name)?.fsColor[
                              currentTheme === 'light' ? 'primary' : 'light'
                            ],
                          })}
                        </Box>
                      }
                    />
                    <Typography variant="body1" mr={3}>
                      {t(`overview.heading.${modules[i].name}` as any)}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
            </Grid>
          </RadioGroup>
          <Typography variant="h3" className="mt-5 mb-2">
            {t('diversity.dimension')}
          </Typography>
          <Grid container spacing={2} my={1}>
            {[...modules[0].contextModels]
              .sort(
                (a, b) =>
                  a.diversityDimension.empiricalConcept.order -
                  b.diversityDimension.empiricalConcept.order,
              )
              .map((cm) => (
                <Grid size={{ xs: 6 }} key={cm.diversityDimension.name}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedDiversityDimensions.includes(cm.diversityDimension.name)}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={
                        <Box mt={1}>
                          {getIcon(cm.diversityDimension.name, {
                            color: diversityDimensionMap.get(cm.diversityDimension.name)?.fsColor[
                              currentTheme === 'light' ? 'primary' : 'light'
                            ],
                          })}
                        </Box>
                      }
                      value={cm.diversityDimension.name}
                    />

                    <Typography variant="body1" mr={3}>
                      {t(`context.model.name.${cm.diversityDimension.name}` as any)}
                    </Typography>
                  </Stack>
                </Grid>
              ))}
          </Grid>
        </>
      ) : null}
      <Typography variant="h3" className="mt-5 mb-4">
        {t('data.from')}
      </Typography>
      <FormControl sx={{ width: 300 }} size="small">
        {runs && runs.length > 0 && (
          <Select
            defaultValue={`${latestRun?.id}`}
            id="run"
            labelId="run-id"
            IconComponent={() => null}
            onChange={(e: SelectChangeEvent) => {
              const curR = runs.filter((r) => r.id === parseInt(e.target.value, 10))[0]
              if (curR) setEndValue(curR.runAt)
            }}
            renderValue={(idr) => {
              const rr = runs.filter((r) => r.id === parseInt(idr, 10))[0]
              return rr ? (
                <Stack flexDirection="row" alignItems="center" justifyContent="space-between">
                  <Typography>{`${formatDate(rr.runAt, currentLanguage, {
                    day: 'numeric',
                    month: 'long',
                    year: 'numeric',
                  })}${rr.id === latestRun?.id ? ` (${t('latest.date')})` : ''}`}</Typography>
                  <CalendarMonthRounded sx={{ mr: -3 }} />
                </Stack>
              ) : null
            }}
          >
            {groupedDateComponents}
          </Select>
        )}
      </FormControl>
      <Typography variant="h3" className="mt-5 mb-4">
        {t('language')}
      </Typography>
      <FormControl>
        <RadioGroup
          defaultValue="de"
          onChange={(event: SelectChangeEvent) => {
            setLocaleValue(event.target.value as 'de' | 'en')
          }}
        >
          <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 3 }}>
            <FormControlLabel
              value="de"
              control={<Radio />}
              label={<span style={{ fontSize: '1rem' }}>{t('german.label')}</span>}
            />
            <FormControlLabel value="en" control={<Radio />} label={<span style={{ fontSize: '1rem' }}>{t('english.label')}</span>} />
          </Stack>
        </RadioGroup>
      </FormControl>
      <Stack sx={{ alignItems: 'flex-end', mt: 2 }}>
        <Button
          disabled={!(selectedModule && selectedDiversityDimensions.length > 0)}
          variant="contained"
          onClick={generateReport}
        >
          <Typography>{t('get.report')}</Typography>
        </Button>
      </Stack>
    </Stack>
  )
}
export default FsGeneralMenu
